import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import ChartSVG, { PLANET_META, PLANET_ORDER, LAGNA_C } from "@/components/ChartSVG";

// Fixed zodiac reference (sign names + traditional lords never change — this is
// labelling, not astro computation; every placement still comes from the engine).
const RASHIS = [
  { en: "Mesha", deva: "मेष", lord: "Mars" }, { en: "Vrishabha", deva: "वृषभ", lord: "Venus" },
  { en: "Mithuna", deva: "मिथुन", lord: "Mercury" }, { en: "Karka", deva: "कर्क", lord: "Moon" },
  { en: "Simha", deva: "सिंह", lord: "Sun" }, { en: "Kanya", deva: "कन्या", lord: "Mercury" },
  { en: "Tula", deva: "तुला", lord: "Venus" }, { en: "Vrishchika", deva: "वृश्चिक", lord: "Mars" },
  { en: "Dhanu", deva: "धनु", lord: "Jupiter" }, { en: "Makara", deva: "मकर", lord: "Saturn" },
  { en: "Kumbha", deva: "कुम्भ", lord: "Saturn" }, { en: "Meena", deva: "मीन", lord: "Jupiter" },
];
const BHAVA_NAMES = ["Tanu", "Dhana", "Sahaja", "Sukha", "Putra", "Ari", "Yuvati", "Ayur", "Dharma", "Karma", "Labha", "Vyaya"];
const ORD = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"];

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-full border border-white/15 bg-white/5 p-1" data-testid={testid}>
    {options.map((o) => (
      <button key={o.v} onClick={() => onChange(o.v)}
        className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${value === o.v ? "bg-amber-400 text-black" : "text-white/70"}`}>
        {o.label}
      </button>
    ))}
  </div>
);

export default function ChartBlock({ chart, caption, pt = (s) => s }) {
  const [variant, setVariant] = useState("north");
  const [varga, setVarga] = useState("d1");
  const [sel, setSel] = useState(null);
  if (!chart?.planets) return null;
  const d9 = varga === "d9";

  const row = (name) => {
    if (name === "Lagna") {
      const l = chart.lagna;
      return { name: "Lagna (Asc)", color: LAGNA_C, retro: false,
        rashi: d9 ? l.d9_rashi : l.rashi, dms: d9 ? l.d9_dms : l.dms,
        nak: d9 ? l.d9_nakshatra : l.nakshatra, pada: d9 ? l.d9_pada : l.pada, house: 1 };
    }
    const o = chart.planets[name]; if (!o) return null;
    return { name, color: PLANET_META[name].c, retro: !!o.retrograde,
      rashi: d9 ? o.d9_rashi : o.rashi, dms: d9 ? o.d9_dms : o.dms,
      nak: d9 ? o.d9_nakshatra : o.nakshatra, pada: d9 ? o.d9_pada : o.pada,
      house: d9 ? o.d9_house : o.house };
  };
  const rows = ["Lagna", ...PLANET_ORDER].map(row).filter(Boolean);

  // Group planets (+ Lagna) into the 12 bhavas for the selected varga — occupants
  // come from each body's engine-given house; signs/lords are the fixed zodiac.
  const lagnaIdx = (d9 ? chart.lagna?.d9_rashi_idx : chart.lagna?.rashi_idx) ?? 0;
  const occ = {};
  rows.forEach((r) => { (occ[r.house] = occ[r.house] || []).push(r); });
  const bhavas = Array.from({ length: 12 }, (_, k) => {
    const sign = RASHIS[(lagnaIdx + k) % 12];
    return { h: k + 1, sign, lord: sign.lord, name: BHAVA_NAMES[k], occ: occ[k + 1] || [] };
  });
  const nodes = rows.filter((r) => r.name === "Rahu" || r.name === "Ketu");
  const st = chart.settings || {};
  const chip = (o) => (o.name === "Lagna (Asc)" ? "Asc" : (PLANET_META[o.name]?.ab || o.name));

  // Open the detail sheet for a planet / the Ascendant (from the list, a chart
  // tap, or a bhava chip). "Asc"/"Lagna" all resolve to the Lagna row.
  const openPlanet = (name) => {
    const target = (name === "Asc" || name === "Lagna" || name === "Lagna (Asc)") ? "Lagna (Asc)" : name;
    const r = rows.find((x) => x.name === target);
    if (r) setSel({ type: "planet", ...r });
  };
  // Open the house card for bhava h — sign, lord, where the lord sits, occupants.
  const openHouse = (h) => {
    const b = bhavas[h - 1];
    if (!b) return;
    const lordRow = rows.find((x) => x.name === b.lord);
    setSel({
      type: "house", h, bhava: b.name,
      signEn: b.sign.en, signDeva: b.sign.deva,
      lord: b.lord, lordColor: PLANET_META[b.lord]?.c || "#e9e2d2",
      lordHouse: lordRow?.house ?? null, lordRashi: lordRow?.rashi ?? null,
      occ: b.occ, isLagna: h === 1,
    });
  };

  return (
    <div data-testid="chart-block">
      {/* Chart-style toggles — aligned to the right */}
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <Seg testid="chart-variant" value={variant} onChange={setVariant}
          options={[{ v: "north", label: "North Indian" }, { v: "south", label: "South Indian" }]} />
        <Seg testid="chart-varga" value={varga} onChange={setVarga}
          options={[{ v: "d1", label: "D1 · Rashi" }, { v: "d9", label: "D9 · Navamsha" }]} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
        <ChartSVG chart={chart} variant={variant} varga={varga} onHouseClick={openHouse} onPlanetClick={openPlanet} />
      </div>
      {caption && <p className="mt-2 text-center text-xs text-white/50">{caption}</p>}
      <p className="mt-1 text-center text-[11px] text-white/35">Tap any house or planet for details</p>

      {/* Planetary Details — Lagna + all nine grahas, straight from the engine */}
      <div className="mt-5">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300/70" data-testid="planetary-details-title">Planetary Details</p>
        <div className="space-y-2">
          {rows.map((r) => (
            <button key={r.name} onClick={() => setSel({ type: "planet", ...r })} data-testid={`planet-row-${r.name}`}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:border-amber-400/50">
              <span className="min-w-0">
                <span className="font-medium" style={{ color: r.color }}>{pt(r.name)}</span>
                <span className="block text-xs text-white/50">{pt(r.nak) || "Not available"}{r.pada != null ? ` · Pada ${r.pada}` : ""}</span>
              </span>
              <span className="shrink-0 text-right text-sm text-white/80">{pt(r.rashi) || "—"}<span className="block text-xs text-white/50">{r.dms} · H{r.house}</span></span>
            </button>
          ))}
        </div>
      </div>

      {/* Ayanamsa · Houses · Node — chart settings tiles, below the planetary list */}
      {(st.ayanamsa || st.ayanamsa_value != null) && (
        <div className="mt-5 grid grid-cols-3 gap-2.5" data-testid="chart-ayanamsa">
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide text-amber-300/70">Ayanamsa</p>
            <p className="mt-1 text-sm font-semibold text-white">{st.ayanamsa || "—"}</p>
            {st.ayanamsa_value != null && <p className="text-[11px] text-white/55">{Number(st.ayanamsa_value).toFixed(3)}°</p>}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide text-amber-300/70">Houses</p>
            <p className="mt-1 text-sm font-medium capitalize text-white/90">{st.houses || "Whole sign"}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide text-amber-300/70">Node</p>
            <p className="mt-1 text-sm font-medium capitalize text-white/90">{st.node || "Mean"}</p>
          </div>
        </div>
      )}

      {/* Bhavas (Houses) grid */}
      <div className="mt-6" data-testid="chart-bhavas">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300/70">Bhavas · Houses {d9 ? "(D9 · Navamsha)" : "(D1 · Rashi)"}</p>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
          {bhavas.map((b) => (
            <button key={b.h} type="button" onClick={() => openHouse(b.h)} data-testid={`bhava-${b.h}`}
              className="rounded-2xl border border-white/10 bg-white/5 p-3 text-left transition-colors hover:border-amber-400/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-300">{ORD[b.h - 1]} House</span>
                <span className="text-[10px] uppercase tracking-wide text-white/40">{b.name}</span>
              </div>
              <p className="mt-1.5 text-sm text-white">{pt(b.sign.en)}</p>
              <p className="text-[11px] text-white/50">{pt("Lord")} · <span style={{ color: PLANET_META[b.lord]?.c || "#e9e2d2" }}>{pt(b.lord)}</span></p>
              <div className="mt-2 flex min-h-[20px] flex-wrap gap-1">
                {b.occ.length > 0
                  ? b.occ.map((o) => (
                      <span key={o.name} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium" style={{ color: o.color }}>
                        {chip(o)}{o.retro ? " (R)" : ""}
                      </span>
                    ))
                  : <span className="text-[10px] text-white/25">Empty</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {sel && createPortal(
        <div className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/60" onClick={() => setSel(null)} data-testid="chart-detail-sheet">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border-t border-white/15 bg-[#121829] p-6 pb-[calc(32px+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
            {sel.type === "house" ? (
              <div data-testid="house-detail">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-serif-latin text-xl text-amber-300">
                    {ORD[sel.h - 1]} House · {pt(sel.signEn)} <span className="text-white/50">({sel.signDeva})</span>
                    {sel.isLagna && <span className="ml-2 rounded-full bg-amber-400/20 px-2 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-amber-300">Lagna</span>}
                  </h3>
                  <button onClick={() => setSel(null)} className="rounded-full p-1.5 text-white/60 hover:bg-white/10"><X className="h-5 w-5" /></button>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div><dt className="text-[11px] uppercase tracking-wide text-amber-300/70">{pt("Bhava")}</dt><dd className="mt-0.5 text-white/90">{sel.bhava}</dd></div>
                  <div><dt className="text-[11px] uppercase tracking-wide text-amber-300/70">{pt("Rashi")}</dt><dd className="mt-0.5 text-white/90">{pt(sel.signEn)}</dd></div>
                  <div><dt className="text-[11px] uppercase tracking-wide text-amber-300/70">{pt("House lord")}</dt><dd className="mt-0.5" style={{ color: sel.lordColor }}>{pt(sel.lord)}</dd></div>
                  <div><dt className="text-[11px] uppercase tracking-wide text-amber-300/70">{pt("Lord placed in")}</dt><dd className="mt-0.5 text-white/90">{sel.lordHouse != null ? `${ORD[sel.lordHouse - 1]} House` : "—"}{sel.lordRashi ? ` · ${pt(sel.lordRashi)}` : ""}</dd></div>
                </dl>
                <div className="mt-4">
                  <p className="mb-1.5 text-[11px] uppercase tracking-wide text-amber-300/70">{pt("Planets here")}</p>
                  {sel.occ.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {sel.occ.map((o) => (
                        <button key={o.name} type="button" onClick={() => openPlanet(o.name)} data-testid={`house-chip-${o.name}`}
                          className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium transition-colors hover:border-amber-400/60" style={{ color: o.color }}>
                          {o.name === "Lagna (Asc)" ? "Asc" : pt(o.name)}{o.retro ? " (R)" : ""}
                        </button>
                      ))}
                    </div>
                  ) : <p className="text-sm text-white/40">No planets</p>}
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-serif-latin text-xl" style={{ color: sel.color }}>{pt(sel.name)}</h3>
                  <button onClick={() => setSel(null)} className="rounded-full p-1.5 text-white/60 hover:bg-white/10"><X className="h-5 w-5" /></button>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {[[pt("Rashi"), pt(sel.rashi)], [pt("Degree"), sel.dms], [`${pt("Nakshatra")} · ${pt("Pada")}`, `${pt(sel.nak) || "—"} · ${sel.pada ?? "—"}`], [pt("House"), sel.house != null ? `${ORD[sel.house - 1]} House` : "—"], ...(["Lagna (Asc)", "Sun", "Moon"].includes(sel.name) ? [] : [[pt("Retrograde"), sel.retro ? "Yes (R)" : "No"]])].map(([k, v]) => (
                    <div key={k}><dt className="text-[11px] uppercase tracking-wide text-amber-300/70">{k}</dt><dd className="mt-0.5 text-white/90">{v === 0 || v ? v : "Not available"}</dd></div>
                  ))}
                </dl>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
